/**
 * Shared formatting utilities for the Commercial ERP.
 * Supports fully dynamic currency codes and locales configured per tenant.
 */

/**
 * Formats a numeric amount as a currency string using the specified code.
 * Defaults to "USD" if no currency code is provided.
 * e.g. formatCurrency(1234.5, "USD") → "$1,234.50"
 * e.g. formatCurrency(1234.5, "EUR") → "€1,234.50"
 */
export function formatCurrency(
  amount: number,
  currencyCode: string = "USD",
  locale: string = "en-US"
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // Fallback if locale or currency code is unsupported
    return `${currencyCode || "$"} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

/** @deprecated Use formatCurrency(amount, currencyCode) instead. */
export function formatKES(amount: number): string {
  return formatCurrency(amount, "USD");
}

/**
 * Formats a timestamp (Date, ISO string, epoch ms, or serialized timestamp)
 * as a localised date string.
 */
export function formatDate(
  ts: Date | string | number | { _seconds?: number; seconds?: number } | null | undefined,
  locale: string = "en-US"
): string {
  if (!ts) return "—";
  let date: Date;
  if (ts instanceof Date) {
    date = ts;
  } else if (typeof ts === "object" && "_seconds" in ts && typeof ts._seconds === "number") {
    date = new Date(ts._seconds * 1000);
  } else if (typeof ts === "object" && "seconds" in ts && typeof ts.seconds === "number") {
    date = new Date(ts.seconds * 1000);
  } else {
    date = new Date(ts as string | number);
  }
  if (isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

/**
 * Formats a number with thousands separators and up to 2 decimal places.
 */
export function formatNumber(n: number, locale: string = "en-US"): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
}
